import type { CloudflareAiComponents } from "@p0security/integrations-cloudflare-ai-shared/types";
import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import type React from "react";

import {
  INSTALL_TOKEN_ENV,
  type InstallContext,
  awsStoreInstallToken,
  awsTf,
  gcpStoreInstallToken,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  CloudflareAiComponents["credential"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<CloudflareAiComponents>["components"]["credential"]
    >["instructions"]
  >
>;

/** What a secret manager contributes to the credential install step. Methods,
 * not properties, let an implementation narrow to its own member. */
type CredentialIac<T extends SecretManagerType = SecretManagerType> = {
  instructions(
    context: InstallContext,
    itemId: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): InstallCommands;
};

/** The setup steps around one cloud's connector Terraform and its command
 * that stores the install token. */
const connectorInstructions = ({
  tf,
  storeInstallToken,
}: {
  tf: string;
  storeInstallToken: string;
}): InstallCommands => ({
  commands: {
    iac: [
      {
        header:
          "Apply the following Terraform to deploy P0's Cloudflare connector.",
        command: tf,
      },
      {
        header: `Have a Super Administrator of the Cloudflare account create an account API token with only Account API Tokens Edit on the account and Zone Read on all zones of the account. Set ${INSTALL_TOKEN_ENV} to the token and run this command to store it, then click "Next".`,
        command: storeInstallToken,
      },
    ],
  },
});

const gcpSmIac: CredentialIac = {
  instructions: (context, itemId, secretManager) =>
    connectorInstructions({
      tf: gcpTf(context, itemId, secretManager),
      storeInstallToken: gcpStoreInstallToken(itemId, secretManager),
    }),
} satisfies CredentialIac<"gcp-sm">;

const awsSmIac: CredentialIac = {
  instructions: (context, itemId, secretManager) =>
    connectorInstructions({
      tf: awsTf(context, itemId, secretManager),
      storeInstallToken: awsStoreInstallToken(itemId, secretManager),
    }),
} satisfies CredentialIac<"aws-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: CredentialIac = {
  instructions: () => ({ commands: {} }),
} satisfies CredentialIac<"azure-kv">;

const credentialIac: Record<SecretManagerType, CredentialIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const NewPreamble: React.FC = () => (
  <>
    <Paragraph>
      P0 gives each AI agent its own short-lived Cloudflare API token, with only
      the permission groups its requests name, through a connector that you
      deploy in AWS or Google Cloud.
    </Paragraph>
    <Paragraph>
      The connector mints those tokens with an install token that you create.
      The install token can give an agent any permission in the account except
      token management, so protect it like a Super Administrator credential.
    </Paragraph>
  </>
);

export const cloudflareAiInstaller: FrontendInstaller<CloudflareAiComponents> =
  {
    components: {
      credential: {
        newPreamble: () => <NewPreamble />,
        instructions: (context, id, item) =>
          credentialIac[item.secretManager.type].instructions(
            context,
            id,
            item.secretManager
          ),
        items: {
          secretManager: {
            options: {
              "aws-sm": {
                items: {
                  install: {
                    optionProvider: otherInstalledIntegration({
                      integration: "aws-sm",
                      component: "iam-write",
                    }),
                  },
                },
              },
              "gcp-sm": {
                items: {
                  install: {
                    optionProvider: otherInstalledIntegration({
                      integration: "gcp-sm",
                      component: "iam-write",
                    }),
                  },
                },
              },
            },
          },
        },
      },
    },
  };
